import { useState, useEffect } from 'react';
import { Mail, Plus, X, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DialogImportEmails from '@/pages/procedures/index/_partials/dialogs/DialogImportEmails';
import { useTranslation } from 'react-i18next';

export const FORM_REPORT_SEND_ID = 'form-report-send';

export default function FormReportSend({ report, onSubmit, onEmailsChange }) {
    const { t } = useTranslation();
    const [emailInput, setEmailInput] = useState('');
    const [emails, setEmails] = useState([]);
    const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);

    useEffect(() => {
        onEmailsChange?.(emails);
    }, [emails, onEmailsChange]);

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const addEmail = () => {
        const trimmedEmail = emailInput.trim().toLowerCase();
        if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
            setEmails([...emails, trimmedEmail]);
            setEmailInput('');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail();
        }
    };

    const removeEmail = (emailToRemove) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
    };

    const handleImportEmails = (importedEmails) => {
        // Filtrer les emails valides et non dupliqués
        const newEmails = importedEmails
            .map((e) => e.trim().toLowerCase())
            .filter((email) => email && isValidEmail(email) && !emails.includes(email));

        setEmails([...emails, ...newEmails]);
        setIsImportDialogOpen(false);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(emails);
    };

    return (
        <>
            <form id={FORM_REPORT_SEND_ID} onSubmit={handleSubmit} className="space-y-[20px]">
                {/* Report Info */}
                <div className="bg-card rounded-[12px] p-[16px]">
                    <p className=" text-[12px] text-muted-foreground mb-[4px]">{t('procedures:rapport')}</p>
                    <p className=" text-[15px] text-foreground">{report.title}</p>
                </div>

                {/* Destinataires */}
                <div>
                    <h3 className=" text-[15px] text-foreground mb-[12px]">{t('procedures:destinataires')}</h3>

                    <div className="bg-card rounded-[12px] p-[14px]">
                        {/* Email Input */}
                        <div className="flex gap-[8px] mb-[12px]">
                            <input
                                type="email"
                                placeholder={t('procedures:ajouterUneAdresseEmail')}
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="flex-1 bg-muted rounded-[8px] px-[12px] h-[40px]  text-[13px] text-foreground placeholder:text-muted-foreground outline-none border border-border focus:border-secondary transition-colors"
                            />
                            <Button
                                type="button"
                                onClick={addEmail}
                                disabled={!emailInput.trim() || !isValidEmail(emailInput.trim())}
                                variant="solid"
                                color="secondary"
                                className="rounded-[8px] h-[40px] w-[40px] p-0 disabled:opacity-50"
                            >
                                <Plus className="h-5 w-5" strokeWidth={2.5} />
                            </Button>
                        </div>

                        {/* Import Button */}
                        <Button
                            type="button"
                            variant="plain"
                            onClick={() => setIsImportDialogOpen(true)}
                            className="w-full bg-muted rounded-[10px] py-[10px] mb-[12px] hover:bg-border h-auto flex items-center justify-center gap-[6px]"
                        >
                            <Upload className="h-4 w-4 text-foreground" />
                            <span className=" text-[13px] text-foreground">{t('procedures:importerUneListeDEmails')}</span>
                        </Button>

                        {/* Email List */}
                        {emails.length > 0 && (
                            <div className="space-y-[6px]">
                                {emails.map((email) => (
                                    <div
                                        key={email}
                                        className="flex items-center justify-between bg-muted rounded-[8px] px-[12px] py-[8px]"
                                    >
                                        <div className="flex items-center gap-[8px] flex-1 min-w-0">
                                            <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                            <span className=" text-[12px] text-foreground truncate">{email}</span>
                                        </div>
                                        <Button
                                            type="button"
                                            variant="plain"
                                            size="icon"
                                            onClick={() => removeEmail(email)}
                                            className="text-destructive hover:text-destructive/70 hover:bg-transparent ml-[8px] h-auto w-auto p-0"
                                        >
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Count */}
                    {emails.length > 0 && (
                        <p className=" text-[12px] text-muted-foreground mt-[8px] text-center">
                            {emails.length} destinataire{emails.length > 1 ? 's' : ''}
                        </p>
                    )}
                </div>
            </form>

            {/* Import Dialog */}
            <DialogImportEmails
                open={isImportDialogOpen}
                onClose={() => setIsImportDialogOpen(false)}
                onImport={handleImportEmails}
            />
        </>
    );
}
